import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { History, FileText, PhoneCall, BadgeCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { inr } from "@/lib/calc";

const ICON = {
  enquiry: { i: FileText, cls: "bg-plum-900 text-white" },
  followup: { i: PhoneCall, cls: "bg-amber-100 text-amber-800" },
  payment: { i: BadgeCheck, cls: "bg-gold-500 text-plum-950" },
};
const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Event = ({ e, close }) => {
  const { i: Icon, cls } = ICON[e.type];
  return (
    <li className="relative pb-5 pl-11" data-testid={`timeline-event-${e.type}`}>
      <span className={`absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full ${cls}`}><Icon className="h-4 w-4" /></span>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="font-display text-sm font-bold text-plum-950">{e.title}</span>
        <span className="text-xs font-semibold text-slate-600">{fmt(e.date)}</span>
      </div>
      <div className="mt-0.5 text-xs text-slate-600">
        <Link to={`/receipts/${e.receipt_id}`} onClick={close} className="font-num font-semibold text-plum-800 hover:underline">{e.receipt_no}</Link>
        {e.by && <> · by {e.by}</>}
      </div>
      {e.type === "enquiry" && (
        <div className="mt-1.5 rounded-md bg-plum-50 px-3 py-2 text-xs text-slate-700">
          <div>{e.child || "—"} · {e.parent || "—"}{e.sub && <span className="capitalize"> · {e.sub.replace(/([A-Z])/g, " $1").toLowerCase()}</span>}</div>
          <div className="mt-1 flex flex-wrap gap-x-4">
            <span>At admission <b className="font-num">₹ {inr(e.amount)}</b></span>
            <span>School fee <b className="font-num">₹ {inr(e.school_fee)}</b></span>
          </div>
        </div>
      )}
      {e.type === "followup" && (e.note || e.next_date) && (
        <div className="mt-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-slate-700">
          {e.note && <div>“{e.note}”</div>}
          {e.next_date && <div className="mt-0.5 text-slate-500">Next follow-up: {fmt(e.next_date)}</div>}
        </div>
      )}
      {e.type === "payment" && (
        <div className="mt-1.5 rounded-md border border-gold-300 bg-gold-50 px-3 py-2 text-xs text-slate-700">
          Received <b className="font-num text-gold-800">₹ {inr(e.amount)}</b>{e.reference && <> · Ref {e.reference}</>}
        </div>
      )}
    </li>
  );
};

export const FamilyTimeline = ({ query, size = "sm", variant = "outline" }) => {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["family-timeline", query],
    queryFn: () => api.post("/receipts/family-timeline", query).then((r) => r.data),
    enabled: open,
  });
  const close = () => setOpen(false);
  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)} data-testid="family-timeline-btn">
        <History className="mr-1.5 h-4 w-4" />Family timeline
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="family-timeline-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-xl font-bold text-plum-950">Family timeline</SheetTitle>
            <SheetDescription>Every enquiry, follow-up and payment for this family, newest first.</SheetDescription>
          </SheetHeader>
          {isLoading || !data ? (
            <div className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-plum-700" /></div>
          ) : (
            <>
              <div className="my-5 grid grid-cols-4 gap-2 text-center" data-testid="family-timeline-stats">
                {[["Visits", data.visits], ["Follow-ups", data.followups], ["Admitted", data.admitted], ["Paid ₹", inr(data.paid)]].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-plum-900/10 bg-white px-2 py-2">
                    <div className="font-num text-base font-bold text-plum-950">{v}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
                  </div>
                ))}
              </div>
              <ol className="relative before:absolute before:bottom-4 before:left-4 before:top-2 before:w-px before:bg-plum-900/15" data-testid="family-timeline-list">
                {data.events.map((e, idx) => <Event key={`${e.type}-${e.receipt_id}-${idx}`} e={e} close={close} />)}
              </ol>
              {!data.events.length && <p className="py-8 text-center text-sm text-slate-500">No history found.</p>}
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
